// macadamia-app/src/macadamia-core/components/postits/PostIt.tsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
    IPostIt,
    POSTIT_COLORS,
    POSTIT_DEFAULTS,
    migratePostItToAnchor,
    viewportToContainer,
    containerToViewport,
    calculateRelativeOffset
} from './types';
import { useSpatial } from '../../contexts/SpatialContext';
import PostItColorPicker from './PostItColorPicker';
import PostItVisibilityPicker from './PostItVisibilityPicker';
import Globe from '../../icons/Globe';
import LockClosed from '../../icons/LockClosed';
import UserGroup from '../../icons/UserGroup';

interface PostItProps {
    postit: IPostIt;
    onChange: (updatedPostIt: IPostIt) => void;
    onDelete: (postItId: string) => void;
    onColorChange: (postItId: string, color: IPostIt['color']) => void;
    onVisibilityChange: (postItId: string, visibility: IPostIt['visibility']) => void;
    containerBounds: DOMRect | null;
    scrollContainer: HTMLElement | null;
}

const PostIt: React.FC<PostItProps> = ({
    postit,
    onChange,
    onDelete,
    onColorChange,
    onVisibilityChange,
    containerBounds,
    scrollContainer
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [currentSize, setCurrentSize] = useState(postit.size);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });
    const [isVisibilityPickerOpen, setIsVisibilityPickerOpen] = useState(false);
    const [visibilityPickerPosition, setVisibilityPickerPosition] = useState({ top: 0, left: 0 });

    const postItRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

    const { getBlockPosition, getNearestBlock, blocks } = useSpatial();

    // Migrer automatiquement l'ancien format si nécessaire
    const migratedPostit = useMemo(() => migratePostItToAnchor(postit), [postit]);

    // State pour forcer le recalcul de position lors du resize
    const [resizeTrigger, setResizeTrigger] = useState(0);

    // ========================================
    // CALCUL DE POSITION DEPUIS L'ANCRAGE
    // ========================================

    // Helper pour obtenir le scroll actuel (container ou window)
    const getScroll = useCallback(() => {
        if (scrollContainer) {
            return { scrollX: scrollContainer.scrollLeft, scrollY: scrollContainer.scrollTop };
        }
        return { scrollX: window.scrollX, scrollY: window.scrollY };
    }, [scrollContainer]);

    const calculatedPosition = useMemo(() => {
        const anchor = migratedPostit.anchor;
        const { scrollX, scrollY } = getScroll();

        // Cas 1 : Ancrage à un bloc
        if (anchor.type === 'block' && anchor.blockId && anchor.relativeOffset) {
            const blockPosition = getBlockPosition(anchor.blockId);

            if (blockPosition) {
                // blockPosition.bounds est en coordonnées viewport
                // Convertir en coordonnées container en ajoutant le scroll du container
                // car on utilise position: absolute dans le container scrollable
                const containerPos = viewportToContainer(
                    blockPosition.bounds.left + anchor.relativeOffset.x,
                    blockPosition.bounds.top + anchor.relativeOffset.y,
                    containerBounds,
                    scrollContainer
                );
                return containerPos;
            } else {
                // Bloc n'existe plus → Fallback sur position document si disponible
                console.warn(`Post-it ${migratedPostit._id}: Bloc ${anchor.blockId} introuvable`);

                // Essayer de récupérer une position de secours
                if (anchor.documentPosition) {
                    // Position stockée = coordonnées container, pas de conversion
                    return {
                        x: anchor.documentPosition.x,
                        y: anchor.documentPosition.y
                    };
                }
            }
        }

        // Cas 2 : Ancrage à une page (non implémenté — fallback sur document)
        if (anchor.type === 'page' && anchor.pageIndex !== undefined && anchor.relativePosition) {
            console.warn(`Post-it ${migratedPostit._id}: Ancrage par page non implémenté, fallback sur document`);
        }

        // Cas 3 : Position absolue dans le container (anciennement "document")
        if (anchor.type === 'document' && anchor.documentPosition) {
            // Les positions sont maintenant stockées en coordonnées container
            // Pas besoin de conversion, juste contraindre aux limites
            const posX = anchor.documentPosition.x;
            const posY = anchor.documentPosition.y;

            // Contraindre pour éviter les débordements (optionnel)
            if (containerBounds) {
                // Utiliser scrollWidth/Height pour permettre le placement dans tout le contenu scrollable
                const maxX = (scrollContainer?.scrollWidth || containerBounds.width) - currentSize.width;
                const maxY = (scrollContainer?.scrollHeight || containerBounds.height) - currentSize.height;

                return {
                    x: Math.max(0, Math.min(posX, maxX)),
                    y: Math.max(0, Math.min(posY, maxY))
                };
            }

            return { x: posX, y: posY };
        }

        // Fallback final : centre du container visible
        if (containerBounds) {
            return {
                x: scrollX + (containerBounds.width - currentSize.width) / 2,
                y: scrollY + (containerBounds.height - currentSize.height) / 2
            };
        }

        return {
            x: (window.innerWidth - currentSize.width) / 2,
            y: (window.innerHeight - currentSize.height) / 2
        };
    }, [migratedPostit, getBlockPosition, currentSize, blocks, resizeTrigger, containerBounds, scrollContainer, getScroll]);

    // Sync avec size
    useEffect(() => {
        setCurrentSize(postit.size);
    }, [postit.size]);

    // ========================================
    // DRAG HANDLERS
    // ========================================

    const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        // Ne pas démarrer le drag si on clique sur le textarea
        if ((e.target as HTMLElement).tagName === 'TEXTAREA') {
            return;
        }

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

        // Convertir clientX/Y (viewport) en coordonnées container
        const containerPos = viewportToContainer(clientX, clientY, containerBounds, scrollContainer);

        setIsDragging(true);
        setDragOffset({
            x: containerPos.x - calculatedPosition.x,
            y: containerPos.y - calculatedPosition.y
        });
    }, [calculatedPosition, containerBounds, scrollContainer]);

    // Variable d'état temporaire pour le drag (position durant le drag)
    const [tempDragPosition, setTempDragPosition] = useState<{x: number; y: number} | null>(null);

    const handleDrag = useCallback((e: MouseEvent | TouchEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

        // Convertir en coordonnées container
        const containerPos = viewportToContainer(clientX, clientY, containerBounds, scrollContainer);

        let newX = containerPos.x - dragOffset.x;
        let newY = containerPos.y - dragOffset.y;

        // Contraindre au conteneur (coordonnées sont maintenant relatives au container)
        if (containerBounds && scrollContainer) {
            // Les limites sont maintenant en coordonnées container (0 à scrollWidth/Height)
            const maxX = scrollContainer.scrollWidth - currentSize.width;
            const maxY = scrollContainer.scrollHeight - currentSize.height;

            // Contraindre le post-it dans le container
            newX = Math.max(0, Math.min(newX, maxX));
            newY = Math.max(0, Math.min(newY, maxY));
        }

        setTempDragPosition({ x: newX, y: newY });
    }, [isDragging, dragOffset, currentSize, containerBounds, scrollContainer]);

    const handleDragEnd = useCallback(() => {
        if (!isDragging || !tempDragPosition) {
            setIsDragging(false);
            setTempDragPosition(null);
            return;
        }

        setIsDragging(false);

        // tempDragPosition est déjà en coordonnées container
        const containerPos = { x: tempDragPosition.x, y: tempDragPosition.y };

        // Convertir en viewport pour getNearestBlock (qui utilise getBoundingClientRect)
        const viewportPos = containerToViewport(
            tempDragPosition.x,
            tempDragPosition.y,
            containerBounds,
            scrollContainer
        );

        // Chercher le bloc le plus proche (en coordonnées viewport)
        const nearestBlock = getNearestBlock(viewportPos.x, viewportPos.y);

        let newAnchor = migratedPostit.anchor;

        if (nearestBlock) {
            // Convertir les centres en coordonnées container pour comparaison
            const blockContainerPos = viewportToContainer(
                nearestBlock.bounds.left + nearestBlock.bounds.width / 2,
                nearestBlock.bounds.top + nearestBlock.bounds.height / 2,
                containerBounds,
                scrollContainer
            );
            const postItCenterX = tempDragPosition.x + currentSize.width / 2;
            const postItCenterY = tempDragPosition.y + currentSize.height / 2;

            const distance = Math.sqrt(
                Math.pow(postItCenterX - blockContainerPos.x, 2) +
                Math.pow(postItCenterY - blockContainerPos.y, 2)
            );

            // Si assez proche, ancrer au bloc
            if (distance < POSTIT_DEFAULTS.ANCHOR_PROXIMITY_THRESHOLD) {
                // calculateRelativeOffset attend des coords viewport
                const relativeOffset = calculateRelativeOffset(
                    viewportPos.x,
                    viewportPos.y,
                    nearestBlock.bounds
                );

                newAnchor = {
                    type: 'block',
                    blockId: nearestBlock.id,
                    relativeOffset,
                    documentPosition: containerPos // Backup au cas où le bloc disparaît
                };

            } else {
                // Trop loin → Ancrage document (coordonnées container)
                newAnchor = {
                    type: 'document',
                    documentPosition: containerPos
                };
            }
        } else {
            // Pas de bloc → Ancrage document (coordonnées container)
            newAnchor = {
                type: 'document',
                documentPosition: containerPos
            };
        }

        // Sauvegarder avec le nouvel ancrage
        onChange({
            ...migratedPostit,
            anchor: newAnchor,
            updatedAt: new Date()
        });

        setTempDragPosition(null);
    }, [isDragging, tempDragPosition, migratedPostit, onChange, getNearestBlock, currentSize, containerBounds, scrollContainer]);

    // ========================================
    // RESIZE HANDLERS
    // ========================================

    const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

        setIsResizing(true);
        resizeStartRef.current = {
            x: clientX,
            y: clientY,
            width: currentSize.width,
            height: currentSize.height
        };
    }, [currentSize]);

    const handleResize = useCallback((e: MouseEvent | TouchEvent) => {
        if (!isResizing) return;

        e.preventDefault();

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

        const deltaX = clientX - resizeStartRef.current.x;
        const deltaY = clientY - resizeStartRef.current.y;

        let newWidth = resizeStartRef.current.width + deltaX;
        let newHeight = resizeStartRef.current.height + deltaY;

        // Contraintes de taille
        newWidth = Math.max(POSTIT_DEFAULTS.MIN_WIDTH, Math.min(POSTIT_DEFAULTS.MAX_WIDTH, newWidth));
        newHeight = Math.max(POSTIT_DEFAULTS.MIN_HEIGHT, Math.min(POSTIT_DEFAULTS.MAX_HEIGHT, newHeight));

        setCurrentSize({ width: newWidth, height: newHeight });
    }, [isResizing]);

    const handleResizeEnd = useCallback(() => {
        if (!isResizing) return;

        setIsResizing(false);

        // Sauvegarder la nouvelle taille
        onChange({
            ...postit,
            size: currentSize,
            updatedAt: new Date()
        });
    }, [isResizing, currentSize, postit, onChange]);

    // ========================================
    // CONTENT HANDLERS
    // ========================================

    const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newContent = e.target.value;

        // Mise à jour optimiste locale
        onChange({
            ...postit,
            content: newContent,
            updatedAt: new Date()
        });
    }, [postit, onChange]);

    const handleDelete = useCallback(() => {
        if (window.confirm('Supprimer ce post-it ?')) {
            onDelete(postit._id);
        }
    }, [postit._id, onDelete]);

    const handleColorButtonClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation(); // Empêcher le drag
        // Position relative au post-it au lieu du viewport
        setColorPickerPosition({
            top: 30,  // 5px (top) + 20px (height du bouton) + 5px (marge)
            left: 5   // Aligné près du bord gauche
        });
        setIsColorPickerOpen(true);
    }, []);

    const handleColorChange = useCallback((color: IPostIt['color']) => {
        onColorChange(postit._id, color);
    }, [postit._id, onColorChange]);

    const handleVisibilityButtonClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation(); // Empêcher le drag
        setVisibilityPickerPosition({
            top: 30,  // 5px (top) + 20px (height du bouton) + 5px (marge)
            left: 5   // Aligné près du bord gauche
        });
        setIsVisibilityPickerOpen(true);
    }, []);

    const handleVisibilityChange = useCallback((visibility: IPostIt['visibility']) => {
        onVisibilityChange(postit._id, visibility);
    }, [postit._id, onVisibilityChange]);

    // ========================================
    // EFFECTS
    // ========================================

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDrag);
            document.addEventListener('touchend', handleDragEnd);

            return () => {
                document.removeEventListener('mousemove', handleDrag);
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchmove', handleDrag);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging, handleDrag, handleDragEnd]);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleResize);
            document.addEventListener('mouseup', handleResizeEnd);
            document.addEventListener('touchmove', handleResize);
            document.addEventListener('touchend', handleResizeEnd);

            return () => {
                document.removeEventListener('mousemove', handleResize);
                document.removeEventListener('mouseup', handleResizeEnd);
                document.removeEventListener('touchmove', handleResize);
                document.removeEventListener('touchend', handleResizeEnd);
            };
        }
    }, [isResizing, handleResize, handleResizeEnd]);

    // Empêcher la sélection de texte pendant le drag
    useEffect(() => {
        const handleSelectStart = (e: Event) => {
            if ((isDragging || isResizing) && e.target !== textareaRef.current) {
                e.preventDefault();
            }
        };

        document.addEventListener('selectstart', handleSelectStart);
        return () => document.removeEventListener('selectstart', handleSelectStart);
    }, [isDragging, isResizing]);

    // ========================================
    // GESTION DU RESIZE DE LA FENÊTRE
    // ========================================

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const handleWindowResize = () => {
            // Debounce de 150ms pour éviter les recalculs frénétiques
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setResizeTrigger(prev => prev + 1);
            }, 150);
        };

        window.addEventListener('resize', handleWindowResize);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    // ========================================
    // STYLES
    // ========================================

    const colorTheme = POSTIT_COLORS[postit.color];

    // Utiliser position temporaire pendant le drag, sinon position calculée
    const displayPosition = tempDragPosition || calculatedPosition;

    const postItStyle: React.CSSProperties = {
        position: 'absolute',
        left: `${displayPosition.x}px`,
        top: `${displayPosition.y}px`,
        width: `${currentSize.width}px`,
        height: `${currentSize.height}px`,
        background: colorTheme.gradient,
        padding: '25px 20px',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        borderRadius: '0 0 2px 2px',
        boxShadow: isDragging
            ? `0 25px 50px rgba(0, 0, 0, 0.25), 0 15px 30px rgba(0, 0, 0, 0.15), inset 0 -2px 10px rgba(0, 0, 0, 0.08)`
            : `rgb(0 0 0 / 0%) 1px 4px 15px, rgb(0 0 0 / 6%) 0px 3px 3px, rgba(0, 0, 0, 0.05) 0px -2px 7px inset`,
        transform: isDragging ? 'rotate(-1deg) scale(1.05)' : 'rotate(-2deg)',
        transition: isDragging || isResizing ? 'none' : 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease',
        zIndex: isDragging ? 20 : 20,
        filter: isDragging ? 'brightness(1.05)' : 'none',
        pointerEvents: 'auto' // Explicitement activer les événements pour ce post-it
    };

    const textareaStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        background: 'transparent',
        border: 'none',
        outline: 'none',
        resize: 'none',
        fontFamily: "'Courier New', monospace",
        fontSize: '16px',
        color: 'rgb(0 0 0)',
        lineHeight: '1.6',
        cursor: 'text',
        textShadow: '0px 1px 0px #ffffff08',
    };

    const resizeHandleStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: '0px',
        right: '0px',
        width: '20px',
        height: '20px',
        cursor: 'nwse-resize',
        zIndex: 10
    };

    const deleteButtonStyle: React.CSSProperties = {
        position: 'absolute',
        top: '5px',
        right: '5px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: 'rgba(0, 0, 0, 0.1)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        color: '#666',
        opacity: 0.5,
        transition: 'opacity 0.2s'
    };

    const visibilityButtonStyle: React.CSSProperties = {
        position: 'absolute',
        top: '5px',
        right: '55px', // Placé à gauche du bouton couleur
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.5)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.6,
        transition: 'opacity 0.2s, transform 0.2s',
        zIndex: 10
    };

    const colorButtonStyle: React.CSSProperties = {
        position: 'absolute',
        top: '5px',
        right: '30px', // 30px pour laisser place au bouton
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: colorTheme.gradient,
        border: '2px solid rgba(255, 255, 255, 0.8)',
        cursor: 'pointer',
        boxShadow: `0 2px 4px ${colorTheme.shadow}`,
        opacity: 0.7,
        transition: 'opacity 0.2s, transform 0.2s',
        zIndex: 10
    };

    // Choisir l'icône en fonction de la visibilité
    const VisibilityIcon = postit.visibility === 'private' ? LockClosed :
                          postit.visibility === 'shared' ? UserGroup :
                          Globe;

    return (
        <div
            ref={postItRef}
            style={postItStyle}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            className="postit"
        >
            {/* Header gradient effect */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '30px',
                    background: `linear-gradient(180deg, ${colorTheme.headerOverlay} 0%, transparent 100%)`,
                    borderRadius: '0 0 2px 2px',
                    pointerEvents: 'none'
                }}
            />

            {/* Visibility picker button */}
            <button
                style={visibilityButtonStyle}
                onClick={handleVisibilityButtonClick}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.6';
                    e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Changer la visibilité"
                className="text-black dark:text-white"
            >
                <VisibilityIcon width="24px" height="24px" fill="currentColor" />
            </button>

            {/* Color picker button */}
            <button
                style={colorButtonStyle}
                onClick={handleColorButtonClick}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.7';
                    e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Changer la couleur"
            />

            {/* Delete button */}
            <button
                style={deleteButtonStyle}
                onClick={handleDelete}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
                title="Supprimer le post-it"
            >
                
            </button>

            {/* Textarea */}
            <textarea
                ref={textareaRef}
                style={textareaStyle}
                value={postit.content}
                onChange={handleContentChange}
                placeholder="Écrivez votre note ici..."
            />

            {/* Resize handle */}
            <div
                style={resizeHandleStyle}
                onMouseDown={handleResizeStart}
                onTouchStart={handleResizeStart}
                title="Redimensionner"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                        d="M15 5L5 15M15 10L10 15"
                        stroke="rgba(0,0,0,0.2)"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                </svg>
            </div>

            {/* Color picker */}
            {isColorPickerOpen && (
                <PostItColorPicker
                    currentColor={postit.color}
                    onColorChange={handleColorChange}
                    onClose={() => setIsColorPickerOpen(false)}
                    position={colorPickerPosition}
                />
            )}

            {/* Visibility picker */}
            {isVisibilityPickerOpen && (
                <PostItVisibilityPicker
                    currentVisibility={postit.visibility}
                    onVisibilityChange={handleVisibilityChange}
                    onClose={() => setIsVisibilityPickerOpen(false)}
                    position={visibilityPickerPosition}
                />
            )}
        </div>
    );
};

export default PostIt;